import React, { useState } from "react";
import { Tabs, Form, Collapse, ConfigProvider } from "antd";
import { useSetterTransform } from "./hooks/useSetterTransform";
import { PanelContainer, TabContent } from "./components/styled";
import { Section } from "./components/Section";
import { useEffects, useFormBinding, useOperations } from "./hooks";
import { PropertyPanelProps } from "./types";
import { DEFAULT_CONFIG, DEFAULT_FORM_LAYOUT, OPERATION_TYPES } from "./constants";
import { OperationButtons } from "./components/OperationButtons";
import { getPropertyPanelTheme, type ThemeType } from './themes';

const PropertyPanel: React.FC<PropertyPanelProps> = ({
  meta,
  onChange,
  config = DEFAULT_CONFIG,
  theme,
  debug = false
}) => {
  // 转换业务 setter 为完整配置
  const transformedMeta = useSetterTransform(meta, debug);

  // 状态管理
  const [form] = Form.useForm();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  // 使用 effects hook
  const { handleEffects } = useEffects(transformedMeta, form);

  // 使用 form binding hook
  const { visibleFields, getSetterConfig, getOptions, handleValuesChange } =
    useFormBinding(transformedMeta, form, handleEffects, onChange!);

  // 使用 operations hook
  const { handleOperation } = useOperations({
    meta: transformedMeta,
    selectedPath,
    onChange,
    form,
    onSelectedPathChange: setSelectedPath,
    debug
  });

  const renderTabContent = (
    section: (typeof transformedMeta.setters.layout)[0]
  ) => {
    return (
      <TabContent>
        <Form
          form={form}
          {...DEFAULT_FORM_LAYOUT}
          {...section.formLayout}
          onValuesChange={handleValuesChange}
        >
          {section.items.map((item: any) => (
            <Collapse key={item.name} defaultActiveKey={[item.name]}>
              <Collapse.Panel
                header={item.title}
                key={item.name}
                extra={
                  item.operations && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <OperationButtons
                        operations={item.operations}
                        onClone={() => handleOperation('CLONE', item.name)}
                        onDelete={() => handleOperation('DELETE', item.name)}
                        onVisibility={() =>
                          handleOperation('VISIBILITY', item.name)
                        }
                      />
                    </div>
                  )
                }
              >
                <Section
                  section={section}
                  item={item}
                  selectedPath={selectedPath}
                  onFieldSelect={setSelectedPath}
                  getSetterConfig={getSetterConfig}
                  getOptions={getOptions}
                  visibleFields={visibleFields}
                  formValues={form.getFieldsValue()}
                />
              </Collapse.Panel>
            </Collapse>
          ))}
        </Form>
      </TabContent>
    );
  };

  const renderTabs = () => {
    return transformedMeta.setters.layout.map((section: any) => ({
      key: section.name,
      label: section.title,
      children: renderTabContent(section),
    }));
  };

  const content = (
    <PanelContainer>
      <Tabs
        defaultActiveKey={transformedMeta.setters.layout[0]?.name}
        items={renderTabs()}
      />
    </PanelContainer>
  );

  // 如果提供了主题，则包装 ConfigProvider
  if (theme) {
    return (
      <ConfigProvider theme={getPropertyPanelTheme(theme)}>
        {content}
      </ConfigProvider>
    );
  }

  return content;
};

export default PropertyPanel;
